import React from 'react'
import Colors from './colors'
import createBreakpoints from '@material-ui/core/styles/createBreakpoints'
import {isIE} from '../../utils'

const UNIT = 12

let direction = 'ltr'
try {
  direction = document.body.getAttribute('dir')
} catch (e) {}

let breakpoints = createBreakpoints({})

const palette = {
  common: {
    white: Colors.White,
    black: Colors.Black
  },
  primary: {
    main: Colors.Grey
  },
  secondary: {
    main: Colors.GrassGreen,
    dark: '#a1bb15'
  },
  error: {
    main: Colors.Red
  },
  action: {
    selected: 'rgba(0, 0, 0, 0.05)',
    active: Colors.DarkGrey
  },
  text: {
    primary: Colors.Grey,
    secondary: Colors.DarkGrey
  }
}

const THEME = {
  palette,
  direction,
  typography: {
    useNextVariants: true,
    fontFamily: 'Roboto',
    fontWeight: 'normal',
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: 'normal',
    letterSpacing: 'normal',
    body2: {
      fontSize: '0.93125rem',
      lineHeight: '1.5rem',
      letterSpacing: 0.3,
      color: palette.text.primary
    },
    body1: {
      fontSize: '0.93125rem',
      lineHeight: '1.35rem',
      letterSpacing: 0.3,
      color: palette.text.secondary
    },
    h6: {
      fontSize: '1.546875rem',
      lineHeight: 1.13,
      letterSpacing: 0.3,
      color: palette.text.primary,
      [breakpoints.down('xs')]: {
        fontSize: `1.3125rem`
      }
    },
    h5: {
      fontSize: '1.85625rem',
      lineHeight: 1.08,
      letterSpacing: 0.2,
      color: palette.text.primary,
      [breakpoints.down('xs')]: {
        fontSize: '1.5rem'
      }
    },
    h3: {
      fontSize: '2.8125rem',
      color: palette.text.primary,
      [breakpoints.down('xs')]: {
        fontSize: '2.25rem'
      }
    },
    h4: {
      fontSize: '2.63125rem',
      letterSpacing: 0.3,
      lineHeight: '1em',
      color: palette.text.primary,
      [breakpoints.down('xs')]: {
        fontSize: '2rem'
      }
    },
    button: {
      fontSize: '0.93125rem',
      lineHeight: '1.08em',
      letterSpacing: 1,
      textAlign: 'center',
      textTransform: 'none',
      color: palette.text.primary
    },
    caption: {
      fontSize: '0.79375rem',
      lineHeight: '1.26em',
      letterSpacing: 0.4,
      color: 'rgba(0,0,0,0.6)'
    },
    subtitle1: {
      fontSize: '1.0625rem',
      lineHeight: '1.41em',
      letterSpacing: 0.2,
      color: palette.text.secondary
    }
  },
  spacing: {
    unit: UNIT
  },
  overrides: {
    MuiAppBar: {
      root: {
        boxShadow: 'none',
        padding: 0,
        paddingLeft: UNIT * 4
      }
    },
    MuiStepConnector: {
      alternativeLabel: {
        top: 19
      }
    },
    MuiStepper: {
      root: {
        paddingTop: 0,
        paddingBottom: UNIT
      }
    },
    MuiChip: {
      root: {
        justifyContent: 'flex-start'
      }
    },
    MuiPaper: {
      rounded: {
        borderRadius: 2
      }
    },
    MuiInput: {
      input: {
        fontSize: '1.0625rem',
        letterSpacing: 0.2,
        color: Colors.DarkGrey
      },
      inputType: {
        height: 'inherit'
      },
      underline: {
        '&:before': {
          borderBottomColor: Colors.DarkGrey
        },
        '&:after': {
          borderBottomColor: Colors.Brand
        }
      }
    },
    MuiSelect: {
      select: {
        fontSize: 'inherit'
      }
    },
    MuiFormLabel: {
      root: {
        fontSize: '1.0625rem',
        fontWeight: 'normal',
        fontStyle: 'normal',
        fontStretch: 'normal',
        lineHeight: '1.41em',
        letterSpacing: 0.2,

        '&$focused': {
          color: Colors.Brand
        }
      },
      focused: {
        '&$focused': {
          color: Colors.Brand
        }
      }
    },
    MuiListItemIcon: {
      root: {
        marginRight: UNIT,
        color: Colors.DarkGrey
      }
    },
    MuiListItem: {
      button: {
        '&:hover': {
          backgroundColor: Colors.Grey008
        }
      }
    },
    MuiStepIcon: {
      root: {
        fontSize: '2.5rem'
      },
      active: {
        color: Colors.Grey + ' !important'
      },
      completed: {
        color: Colors.GrassGreen + ' !important'
      },
      text: {
        fill: Colors.White
      }
    },
    MuiMenuItem: {
      root: {
        paddingLeft: UNIT * 2
      },
      selected: {
        backgroundColor: Colors.PaleTeal015 + ' !important'
      }
    },
    MuiButton: {
      outlined: {
        fontWeight: 'bold',
        textTransform: 'uppercase'
      },
      contained: {
        fontWeight: 'bold',
        borderRadius: 0,
        textTransform: 'uppercase',
        boxShadow:
          '0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 2px 1px -1px rgba(0, 0, 0, 0.12), 0 1px 1px 0 rgba(0, 0, 0, 0.14)',
        padding: `${isIE ? UNIT : 8}px ${UNIT * 2}px`,
        backgroundColor: Colors.DarkGrey,
        color: Colors.White
      },
      containedPrimary: {
        color: Colors.White
      },
      sizeSmall: {
        fontSize: '0.93125rem'
      },
      label: {
        lineHeight: '1.08em'
      }
    },
    MuiToggleButton: {
      root: {
        backgroundColor: Colors.LightGrey
      },
      selected: {
        color: Colors.Black,
        backgroundColor: 'transparent',
        opacity: 1,
        '&:after': {
          backgroundColor: Colors.White
        }
      }
    },
    MuiTouchRipple: {
      root: {
        color: Colors.White
      }
    },
    MuiTableCell: {
      head: {
        fontSize: '0.85rem'
      },
      paddingDense: {
        [breakpoints.down('xs')]: {
          padding: `${UNIT / 3}px ${UNIT}px`,
          paddingRight: `${UNIT}px !important`
        }
      }
    },
    MuiTab: {
      label: {
        fontWeight: 'bold'
      },
      root: {
        maxWidth: 'initial'
      },
      fullWidth: {
        flexShrink: 0
      }
    },
    MuiTableRow: {
      head: {
        height: UNIT * 3
      }
    },
    MuiTypography: {
      root: {
        '& a': {
          color: Colors.Brand,
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline'
          }
        }
      }
    },
    MuiExpansionPanelDetails: {
      root: {
        [breakpoints.down('xs')]: {
          padding: `${UNIT / 2}px ${UNIT}px ${UNIT}px`
        }
      }
    },
    MuiExpansionPanelSummary: {
      expandIcon: {
        [breakpoints.down('xs')]: {
          right: 0
        }
      }
    }
  }
}
const PortalTheme = React.createContext({theme: THEME, onThemeChange: () => {}})

const withThemeSwitch = Component => props => {
  return (
    <PortalTheme.Consumer>
      {onThemeChange => <Component {...props} onThemeChange={onThemeChange} />}
    </PortalTheme.Consumer>
  )
}

// export default createMuiTheme(THEME)
export default PortalTheme
export {UNIT, THEME, withThemeSwitch}



// WEBPACK FOOTER //
// ./src/shared/assets/styles/theme.js